'use client';

import { useEffect, useRef, useState } from 'react';
import type { Job } from '@/features/design/job-queue';

export type RenderOverlayPhase = 'visible' | 'fading' | 'hidden';

/** Bridge between the job leaving the queue and the preload flag taking over —
 *  the two flip via separate WS events with a gap in between (same 100 ms
 *  bridge as Android `smoothActive` / Apple's pre-fade delay). */
const OVERLAY_BRIDGE_MS = 100;
/** Overlay dissolve duration — matches Apple's 1.2 s ease-out fade. */
export const RENDER_OVERLAY_FADE_MS = 1200;

export interface SmoothRenderOverlay {
  /** Job to feed the progress overlay. While fading, this is the last active
   *  job retained after it left the queue. */
  job: Job | undefined;
  phase: RenderOverlayPhase;
}

/**
 * Keeps the render-progress overlay up until the finished render image is
 * decoded, then dissolves it — instead of unmounting the overlay the moment
 * the job leaves the queue (which exposed a loading gap while the render was
 * still downloading).
 *
 * Mirrors Apple `smoothShowOverlay` (AngleImageDisplay.swift) and Android
 * `smoothActive` (RenderImageDisplay.kt): overlay needed = active job OR
 * image preloading; on release wait the bridge, then fade out. Pass the
 * image-dim flag from `phase === 'visible'` so the grayscale→color reveal of
 * the new render starts exactly when the overlay begins to dissolve.
 */
export function useSmoothRenderOverlay(
  activeJob: Job | undefined,
  isPreloading: boolean
): SmoothRenderOverlay {
  const rawActive = !!activeJob || isPreloading;

  // Retain the last active job so the overlay keeps rendering it through the
  // preload + fade-out window (by then the job itself has left the queue).
  const lastJobRef = useRef<Job | undefined>(activeJob);
  if (activeJob) lastJobRef.current = activeJob;

  const [phase, setPhase] = useState<RenderOverlayPhase>(
    rawActive ? 'visible' : 'hidden'
  );
  const wasActiveRef = useRef(rawActive);

  useEffect(() => {
    if (rawActive) {
      wasActiveRef.current = true;
      setPhase('visible');
      return undefined;
    }
    if (!wasActiveRef.current) return undefined; // never shown — nothing to fade
    wasActiveRef.current = false;

    const fadeTimer = setTimeout(() => setPhase('fading'), OVERLAY_BRIDGE_MS);
    const hideTimer = setTimeout(
      () => setPhase('hidden'),
      OVERLAY_BRIDGE_MS + RENDER_OVERLAY_FADE_MS
    );
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [rawActive]);

  return {
    job: activeJob ?? (phase !== 'hidden' ? lastJobRef.current : undefined),
    phase,
  };
}
